import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Container } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type { Tag } from '~/services/TagsService';
import { formatDateWithClock } from '~/services/TimeService';

import { useGetTags } from './hooks';

interface Props {
  onCreate: () => void;
  onEdit: (tagId: string) => void;
}

export default function TagsList({ onCreate, onEdit }: Props) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const {
    data = null,
    isLoading = false,
    error = null,
  } = useGetTags({
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    sortBy: 'updated_at',
    sortOrder: 'desc',
  });

  const columns: Array<GridColDef<Tag>> = [
    {
      field: 'id',
      headerName: 'ID',
      width: 200,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      flex: 1,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      renderCell: (params: GridRenderCellParams<Tag>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.updatedAt ? formatDateWithClock(params.row.updatedAt) : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEdit(params.row.id)}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <Box mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => onCreate()}>
          Create Tag
        </Button>
      </Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <DataGrid
        loading={isLoading}
        rows={data?.result ?? []}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: Tag) => row.id}
        paginationMode="server"
        rowCount={data?.pagination?.total_items ?? 0}
        slots={{ pagination: GridPagination }}
        autoHeight
      />
    </Container>
  );
}
